import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Thumbnail } from '../../Thumbnail';
import { EditorToolbar } from './EditorToolbar';
import { ImageEditorCanvas, MAX_CANVAS_WIDTH, MAX_CANVAS_HEIGHT } from './ImageEditorCanvas';
import {
  applyCropToCanvas,
  applyMaskToCanvas,
  canvasToFile,
  createMaskFromRegions,
  cropToContent,
  loadImage,
} from './imageExportUtils';
import { useImageEditor } from './useImageEditor';
import { useSegmentation } from './useSegmentation';
import { DEFAULT_NUM_SEGMENTS, DEFAULT_SENSITIVITY } from './SegmentTool';
import { useCrop } from './useCrop';
import { CropRect } from './types';

type ImageEditorModalProperties = {
  open: boolean;
  files: File[];
  onComplete: (editedFiles: File[]) => void;
  onCancel: () => void;
};

const FILMSTRIP_THUMB_SIZE = 48;

const FilmstripItem = styled(Box, { shouldForwardProp: property => property !== 'isActive' })<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    width: FILMSTRIP_THUMB_SIZE,
    height: FILMSTRIP_THUMB_SIZE,
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: isActive ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
    opacity: isActive ? 1 : 0.6,
    transition: 'opacity 0.15s, border-color 0.15s',
    '&:hover': { opacity: 1 },
    flexShrink: 0,
  })
);

export const ImageEditorModal = ({ open, files, onComplete, onCancel }: ImageEditorModalProperties) => {
  const {
    currentIndex,
    setCurrentIndex,
    imageStates,
    currentState,
    setActiveTool,
    setCropRect,
    setLassoPoints,
    setSegmentation,
    toggleRegionSelection,
    setEditedFile,
    resetCurrentImage,
    getOutputFiles,
  } = useImageEditor(files);

  const { detectCrop } = useCrop();
  const [numberSegments, setNumberSegments] = useState(DEFAULT_NUM_SEGMENTS);
  const [sensitivity, setSensitivity] = useState(DEFAULT_SENSITIVITY);

  const isSegmentMode = currentState?.activeTool === 'segment';
  const { segmentation, isSegmenting } = useSegmentation({
    file: isSegmentMode ? currentState?.originalFile : undefined,
    numberSegments,
    sensitivity,
  });

  useEffect(() => {
    if (segmentation) {
      setSegmentation(segmentation);
    }
  }, [segmentation]);

  const autoDetectCrop = (file: File) => {
    loadImage(file).then(img => {
      detectCrop(img).then(rect => {
        if (!rect) return;
        setCropRect(rect);
        const canvas = applyCropToCanvas(img, rect);
        canvasToFile(canvas, file.name).then(setEditedFile);
      });
    });
  };

  const handleRegionToggle = async (regionId: number) => {
    if (!currentState?.segmentation) return;

    const nextSelected = new Set(currentState.selectedRegionIds);
    if (nextSelected.has(regionId)) {
      nextSelected.delete(regionId);
    } else {
      nextSelected.add(regionId);
    }

    toggleRegionSelection(regionId);

    if (nextSelected.size === 0) {
      setEditedFile(undefined);
      return;
    }

    const img = await loadImage(currentState.originalFile);
    const mask = createMaskFromRegions(currentState.segmentation, nextSelected, img.naturalWidth, img.naturalHeight);
    const masked = applyMaskToCanvas(img, mask);
    const canvas = cropToContent(masked);
    const file = await canvasToFile(canvas, currentState.originalFile.name);
    setEditedFile(file);
  };

  const handleApplyCrop = async (cropRect: CropRect) => {
    if (!currentState) return;
    const img = await loadImage(currentState.originalFile);
    const canvas = applyCropToCanvas(img, cropRect);
    const file = await canvasToFile(canvas, currentState.originalFile.name);
    setEditedFile(file);
  };

  const handleToolChange = (tool: 'lasso' | 'segment' | 'smartCrop') => {
    setActiveTool(tool);
    if (tool === 'smartCrop' && currentState) {
      autoDetectCrop(currentState.originalFile);
    }
  };

  const previewFile = currentState?.editedFile ?? currentState?.originalFile;
  const previewUrl = useMemo(() => {
    if (!previewFile) return '';
    return URL.createObjectURL(previewFile);
  }, [previewFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const [previewContainerSize, setPreviewContainerSize] = useState({
    width: MAX_CANVAS_WIDTH,
    height: MAX_CANVAS_HEIGHT,
  });

  useEffect(() => {
    if (!currentState?.originalFile) return;
    loadImage(currentState.originalFile).then(img => {
      const scale = Math.min(MAX_CANVAS_WIDTH / img.naturalWidth, MAX_CANVAS_HEIGHT / img.naturalHeight, 1);
      setPreviewContainerSize({
        width: Math.round(img.naturalWidth * scale),
        height: Math.round(img.naturalHeight * scale),
      });
    });
  }, [currentState?.originalFile]);

  if (!open || !currentState) return;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth='lg'
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: '12px', minHeight: '500px', userSelect: 'none' },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Edit Image</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <EditorToolbar
            activeTool={currentState.activeTool}
            onToolChange={handleToolChange}
            isSegmenting={isSegmenting}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', justifyContent: 'center' }}>
          <Box sx={{ flexShrink: 0 }}>
            <ImageEditorCanvas
              file={currentState.originalFile}
              activeTool={currentState.activeTool}
              cropRect={currentState.cropRect}
              lassoPoints={currentState.lassoPoints}
              segmentation={currentState.segmentation}
              selectedRegionIds={currentState.selectedRegionIds}
              onCropChange={setCropRect}
              onCropEnd={rect => {
                void handleApplyCrop(rect);
              }}
              onLassoPointsChange={setLassoPoints}
              onLassoExport={setEditedFile}
              onRegionClick={regionId => {
                void handleRegionToggle(regionId);
              }}
              onToolChange={handleToolChange}
              isSegmenting={isSegmenting}
              numberSegments={numberSegments}
              sensitivity={sensitivity}
              onNumberSegmentsChange={setNumberSegments}
              onSensitivityChange={setSensitivity}
            />
          </Box>

          {previewUrl && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                width: previewContainerSize.width,
              }}
            >
              <Box
                sx={{
                  width: previewContainerSize.width,
                  height: previewContainerSize.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={previewUrl}
                  alt='Search preview'
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Typography variant='caption' color='text.secondary'>
                Search preview
              </Typography>
            </Box>
          )}
        </Box>

        {files.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {imageStates.map((state, index) => {
              const handleFilmstripClick = () => {
                setCurrentIndex(index);
                if (state.activeTool === 'smartCrop' && !state.cropRect) {
                  autoDetectCrop(state.originalFile);
                }
              };
              return (
                <FilmstripItem
                  key={`${state.originalFile.name}-${index}`}
                  isActive={index === currentIndex}
                  onClick={handleFilmstripClick}
                >
                  <Thumbnail file={state.editedFile ?? state.originalFile} size={FILMSTRIP_THUMB_SIZE} noLink />
                </FilmstripItem>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', borderRadius: '12px' }}>
          Cancel
        </Button>
        <Button onClick={resetCurrentImage} sx={{ textTransform: 'none', borderRadius: '12px' }}>
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant='contained'
          onClick={() => onComplete(getOutputFiles())}
          sx={{ textTransform: 'none', borderRadius: '12px' }}
        >
          Search
        </Button>
      </DialogActions>
    </Dialog>
  );
};
